import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCircle, FaStopCircle } from "react-icons/fa";
import "./ScreenRecorderPanel.css";

const formatFileName = () => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `gcc-recording-${timestamp}.webm`;
};

const ScreenRecorderPanel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const hasRecordParam = queryParams.get("record") === "1";
  const captureAvailable = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia;
  const [persistRecordParam, setPersistRecordParam] = useState(hasRecordParam);

  const [isRecording, setIsRecording] = useState(false);
  const [hideCursor, setHideCursor] = useState(false);
  const [statusMessage, setStatusMessage] = useState("Ready to record the page.");
  const [errorMessage, setErrorMessage] = useState("");

  const recorderRef = useRef(null);
  const streamsRef = useRef([]);
  const chunksRef = useRef([]);

  useEffect(() => {
    return () => {
      if (recorderRef.current?.state === "recording") {
        recorderRef.current.stop();
      }
      streamsRef.current.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
      streamsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (hasRecordParam && !persistRecordParam) {
      setPersistRecordParam(true);
    }
  }, [hasRecordParam, persistRecordParam]);

  useEffect(() => {
    if (isRecording && !persistRecordParam) {
      setPersistRecordParam(true);
    }
  }, [isRecording, persistRecordParam]);

  const cleanupStreams = () => {
    streamsRef.current.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
    streamsRef.current = [];
  };

  const saveRecordingBlob = async (blob) => {
    if (!blob || !blob.size) {
      setStatusMessage("No media was captured.");
      return;
    }

    setStatusMessage("Saving recording…");
    const fileName = formatFileName();
    const fileTypes = [
      {
        description: "Screen recording",
        accept: { "video/webm": [".webm"] },
      },
    ];

    const canUseFilePicker = typeof window !== "undefined" && "showSaveFilePicker" in window;
    if (canUseFilePicker) {
      try {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: fileTypes,
          startIn: "desktop",
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        setStatusMessage("Recording saved to desktop.");
        return;
      } catch (err) {
        console.warn("File picker save failed", err);
      }
    }

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
    setStatusMessage("Download ready; please save it to your Desktop.");
  };

  const handleStopRecording = () => {
    if (!recorderRef.current || recorderRef.current.state !== "recording") {
      return;
    }
    setStatusMessage("Finishing recording…");
    recorderRef.current.stop();
  };

  const startRecording = async () => {
    if (!captureAvailable) {
      setErrorMessage("Screen capture is not supported by this browser.");
      return;
    }

    setErrorMessage("");
    setStatusMessage("Requesting access to your screen…");
    try {
      const screenWidth = typeof window !== "undefined" ? window.screen.width : undefined;
      const screenHeight = typeof window !== "undefined" ? window.screen.height : undefined;
      const cursorSetting = hideCursor ? "never" : "always";
      const videoConstraints = {
        cursor: cursorSetting,
        frameRate: { ideal: 30 },
        displaySurface: "browser",
        logicalSurface: true,
      };
      if (screenWidth) {
        videoConstraints.width = { ideal: screenWidth };
      }
      if (screenHeight) {
        videoConstraints.height = { ideal: screenHeight };
      }

      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: videoConstraints,
        audio: true,
      });

      const mixedStream = new MediaStream();
      displayStream.getVideoTracks().forEach((track) => mixedStream.addTrack(track));
      displayStream.getAudioTracks().forEach((track) => mixedStream.addTrack(track));
      streamsRef.current = [displayStream];

      chunksRef.current = [];
      const recorder = new MediaRecorder(mixedStream);
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        setIsRecording(false);
        setStatusMessage("Processing the recording…");
        cleanupStreams();
        const recordingBlob = new Blob(chunksRef.current, { type: "video/webm" });
        chunksRef.current = [];
        saveRecordingBlob(recordingBlob);
      };

      recorder.start();
      setIsRecording(true);
      setStatusMessage("Recording… tap the icon again to stop.");
    } catch (err) {
      console.error("Failed to capture screen", err);
      setErrorMessage("Screen capture canceled or permission denied.");
      cleanupStreams();
      setIsRecording(false);
      setStatusMessage("Ready to try again.");
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      handleStopRecording();
      return;
    }
    startRecording();
  };

  const shouldRender = hasRecordParam || isRecording || persistRecordParam;

  useEffect(() => {
    if ((!persistRecordParam && !isRecording) || hasRecordParam) {
      return;
    }
    const newParams = new URLSearchParams(location.search);
    newParams.set("record", "1");
    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });
  }, [
    hasRecordParam,
    isRecording,
    persistRecordParam,
    location.pathname,
    location.search,
    navigate,
  ]);

  if (!shouldRender) {
    return null;
  }

  return (
    <div className="screen-record-panel" aria-live="polite">
      <button
        className={`screen-record-button ${isRecording ? "recording" : ""}`}
        type="button"
        onClick={toggleRecording}
        disabled={!captureAvailable && !isRecording}
        aria-pressed={isRecording}
      >
        <span className="screen-record-icon">
          {isRecording ? <FaStopCircle size={20} /> : <FaCircle size={20} />}
        </span>
        <span className="screen-record-label">{isRecording ? "Stop recording" : "Record screen"}</span>
      </button>
      <div className="screen-record-details">
        <label className="screen-record-checkbox">
          <input
            type="checkbox"
            checked={hideCursor}
            onChange={(event) => setHideCursor(event.target.checked)}
            disabled={isRecording}
          />
          Hide mouse cursor while recording
        </label>
        <p className="screen-record-status">{statusMessage}</p>
        <p className="screen-record-helper">
          When Chrome asks what to share, pick “This tab,” enable “Share audio,” keep `record=1`, and enable “Hide mouse cursor” so the captured stream omits the pointer while it stays visible on your screen.
        </p>
        <p className="screen-record-helper">
          Only the tab you choose and its system audio are recorded; microphone input is excluded.
        </p>
        {errorMessage && <p className="screen-record-error">{errorMessage}</p>}
      </div>
    </div>
  );
};

export default ScreenRecorderPanel;
